import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    IconButton,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { makeRequest } from '../../../apis/makeRequest'
import { updateTransport, deleteTransportPlate, getTransportDetail } from '../../../apis/backendRequests'
import { notificationActions } from '../../../actions'
import { getApiErrorCode, getApiErrorMessage, transportErrors, transportMaxLengths } from '../../../helpers/logisticaHelper'
import { StyledTextField, DarkButton } from '../../../ui/StyledComponents'
import { useConfirm } from '../ConfirmDialog'
import PatenteDialog from './PatenteDialog'

// Datos web del transporte (TransportSaveDTO); el resto viene de GP y no se edita
const webFields = ['address', 'city', 'state', 'country', 'email', 'notes']

// Sin datos web el primer PUT los crea activos: el switch arranca prendido
const toForm = (transport) => ({
    ...Object.fromEntries(webFields.map((f) => [f, transport?.[f] ?? ''])),
    isActive: transport?.isRegistered ? !!transport.isActive : true,
})

const plateSummary = (p) => [
    p.brandModel,
    p.driverName && `Chofer: ${p.driverName}`,
    p.tareTons != null && `Tara: ${p.tareTons} t`,
    p.volumeM3 != null && `Volumen: ${p.volumeM3} m³`,
    p.axles != null && `Ejes: ${p.axles}`,
].filter(Boolean).join(' · ')

export default function TransporteDialog({ open, transport, onClose, onSaved, onTransportNotFound }) {
    const dispatch = useDispatch()
    const [form, setForm] = useState(toForm(null))
    // null = cerrado, { plate: null } = alta, { plate: TransportPlateDTO } = edición
    const [plateEditing, setPlateEditing] = useState(null)
    const [confirm, confirmDialog] = useConfirm()

    // RETIRA CLIENTE no se puede desactivar (error 502)
    const isRetiraCliente = !!transport?.isRetiraCliente

    // Se reinicia solo al cambiar de transporte: guardar una patente devuelve el mismo transporte y no pisa lo tipeado
    useEffect(() => {
        if (open) setForm(toForm(transport))
    }, [open, transport?.code]) // eslint-disable-line react-hooks/exhaustive-deps

    const saved = toForm(transport)
    const dirty = Object.keys(form).some((k) => form[k] !== saved[k])

    const showError = (err) => {
        dispatch(notificationActions.showError({ message: getApiErrorMessage(err) }))
        if (getApiErrorCode(err) === transportErrors.NOT_FOUND) onTransportNotFound()
    }

    const setField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }))

    const handleGuardar = () => {
        const payload = {
            ...Object.fromEntries(webFields.map((f) => [f, form[f].trim() || null])),
            isActive: form.isActive,
        }
        makeRequest(
            () => updateTransport(transport.code, payload),
            (res) => {
                dispatch(notificationActions.showSuccess({ message: `Transporte ${transport.code} guardado.` }))
                setForm(toForm(res.data))
                onSaved(res.data)
            },
            showError,
            { checkEmptyResults: false }
        )
    }

    const handleCerrar = async () => {
        if (dirty) {
            const ok = await confirm({
                title: 'Cambios sin guardar',
                message: `Los datos de ${transport.code} tienen cambios sin guardar. ¿Descartarlos?`,
                confirmText: 'Descartar',
                cancelText: 'Volver',
            })
            if (!ok) return
        }
        onClose()
    }

    const refrescarTransporte = () => {
        makeRequest(
            () => getTransportDetail(transport.code),
            (res) => onSaved(res.data),
            showError,
            { checkEmptyResults: false }
        )
    }

    const handleQuitarPatente = async (plate) => {
        const ok = await confirm({
            title: 'Quitar patente',
            message: `¿Quitar la patente ${plate} de ${transport.code}?`,
            confirmText: 'Quitar',
            cancelText: 'Volver',
        })
        if (!ok) return
        makeRequest(
            () => deleteTransportPlate(transport.code, plate),
            (res) => onSaved(res.data),
            (err) => {
                showError(err)
                if (getApiErrorCode(err) === transportErrors.PLATE_NOT_FOUND) refrescarTransporte()
            },
            { checkEmptyResults: false }
        )
    }

    if (!transport) return null

    const gpField = (label, value) => (
        <StyledTextField size="small" label={label} value={value ?? ''} disabled />
    )

    const webField = (name, label, props = {}) => (
        <StyledTextField
            size="small"
            label={label}
            value={form[name]}
            onChange={setField(name)}
            inputProps={{ maxLength: transportMaxLengths[name] }}
            {...props}
        />
    )

    return (
        <>
            <Dialog open={open} onClose={handleCerrar} maxWidth="md" fullWidth>
                <DialogTitle>{`Transporte ${transport.code}`}</DialogTitle>
                <DialogContent>
                    {!transport.isInGp && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            Esta forma de envío ya no existe en GP. Se conservan los datos web.
                        </Alert>
                    )}
                    {!transport.isRegistered && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Todavía no tiene datos web: se crean al guardar o al agregar una patente.
                        </Alert>
                    )}

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Datos de GP</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
                        {gpField('Código', transport.code)}
                        {gpField('Tipo de envío', transport.shippingTypeName)}
                        <Box sx={{ gridColumn: { sm: '1 / -1' }, display: 'grid' }}>
                            {gpField('Descripción', transport.description)}
                        </Box>
                        {gpField('Teléfono', transport.phone)}
                        {gpField('Contacto', transport.contact)}
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Datos web</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, pt: 1 }}>
                        {webField('address', 'Dirección', { sx: { gridColumn: { sm: '1 / -1' } } })}
                        {webField('city', 'Ciudad')}
                        {webField('state', 'Provincia')}
                        {webField('country', 'País')}
                        {webField('email', 'Email', { type: 'email' })}
                        {webField('notes', 'Notas', { multiline: true, minRows: 2, sx: { gridColumn: { sm: '1 / -1' } } })}
                    </Box>
                    <FormControlLabel
                        sx={{ mt: 1 }}
                        control={
                            <Switch
                                checked={form.isActive}
                                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                                disabled={isRetiraCliente}
                            />
                        }
                        label="Activo"
                    />

                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2">Patentes</Typography>
                        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setPlateEditing({ plate: null })}>
                            Agregar patente
                        </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {transport.plates.length > 0
                            ? transport.plates.map((p) => (
                                <Tooltip key={p.plate} title={plateSummary(p) || 'Sin datos cargados'}>
                                    <Chip
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                {p.plate}
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Editar patente ${p.plate}`}
                                                    onClick={() => setPlateEditing({ plate: p })}
                                                    sx={{ p: 0.25 }}
                                                >
                                                    <EditIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Box>
                                        }
                                        onDelete={() => handleQuitarPatente(p.plate)}
                                    />
                                </Tooltip>
                            ))
                            : <Typography variant="body2" color="text.secondary">Sin patentes cargadas.</Typography>}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCerrar}>Cerrar</Button>
                    <DarkButton onClick={handleGuardar} disabled={!dirty}>Guardar</DarkButton>
                </DialogActions>
            </Dialog>

            <PatenteDialog
                open={!!plateEditing}
                transport={transport}
                plate={plateEditing?.plate ?? null}
                onClose={() => setPlateEditing(null)}
                onSaved={(updated) => {
                    setPlateEditing(null)
                    onSaved(updated)
                }}
                onTransportNotFound={onTransportNotFound}
            />
            {confirmDialog}
        </>
    )
}
